import { SortDirection } from '@fullstory/components';
import { AuditMetadata, Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import {
  ObjectType,
  SpaceLibraryFilter,
  SpaceObject,
  SpaceObjectOrder,
} from '@fullstory/proto/fs/services/dx-objects';
import { PageInfoResponse } from '@fullstory/proto/fs/shared/pagination';
import { int64 } from '@fullstory/proto/google/protobuf';
import {
  AppDispatch,
  DxObjectsCacheActions,
  FilterOptionValueType,
  fullstorySelectors,
  insightToInsightDetail,
  SpaceRequestActions,
} from '@fullstory/redux';
import {
  LIBRARY_OBJECT_SEARCH_REQUEST_KEY,
  shouldGetDxObjectsRequestSkipCache,
} from '@fullstory/redux/modules/dxObjectsCache';
import { useXhrRequest } from '@fullstory/redux-util';
import { LibraryObjectType } from '@fullstory/routes';
import { isEqual, pick } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  LibraryObject,
  LIBRARY_TABLE_CREATOR_COLUMN_KEY,
  LIBRARY_TABLE_INSIGHTS_COLUMN_KEY,
  LIBRARY_TABLE_LAST_MODIFIED_COLUMN_KEY,
  LIBRARY_TABLE_NAME_COLUMN_KEY,
  LIBRARY_TABLE_SPACE_COLUMN_KEY,
} from '../components/LibraryTable';

type RecordProps = {
  id: string | int64;
  name: string;
  description: string;
};

const ZERO_RESULTS: LibraryObject[] = [];

const toLibraryObject = ({
  spaceObject,
  recordObj,
  canned,
  starred,
  metadataObj,
}: {
  spaceObject: SpaceObject;
  recordObj: RecordProps | null | undefined;
  canned: boolean;
  starred: boolean;
  metadataObj:
    | Pick<
        AuditMetadata,
        'created' | 'createdBy' | 'lastUpdated' | 'lastUpdatedBy' | 'archived' | 'visibility'
      >
    | null
    | undefined;
}): LibraryObject => ({
  id: recordObj?.id as string,
  name: recordObj?.name as string,
  description: recordObj?.description as string,
  objectType: spaceObject.dxObjectType as LibraryObjectType,
  featuredIn: spaceObject.featuredIn,
  referencedIn: spaceObject.referencedIn,
  owningSpace: spaceObject.owningSpace,
  insights: spaceObject.insights.map(insightToInsightDetail),
  // DX object detail entities
  ...pick(spaceObject, [
    'dashboard',
    'funnel',
    'metric',
    'retentionMetric',
    'segment',
    'heatmap',
    'journey',
  ]),
  starred,
  canned,
  canOwn: spaceObject.canOwn?.isAllowed ?? false,
  created: metadataObj?.created ?? null,
  createdBy: metadataObj?.createdBy as string,
  lastUpdated: metadataObj?.lastUpdated ?? null,
  lastUpdatedBy: metadataObj?.lastUpdatedBy as string,
  archived: metadataObj?.archived as boolean,
  visibility: spaceObject.dashboard?.visibility || (metadataObj?.visibility as Visibility),
});

export const toLibraryObjects = (objects: SpaceObject[]) => {
  if (!objects.length) return ZERO_RESULTS;

  return objects.reduce<LibraryObject[]>((acc, spaceObject) => {
    if (spaceObject.metric) {
      acc.push(
        toLibraryObject({
          spaceObject,
          recordObj: spaceObject.metric?.metric?.metric,
          canned: spaceObject.metric?.canned ?? false,
          starred: spaceObject.metric?.starred ?? false,
          metadataObj: spaceObject.metric?.metric?.metadata,
        }),
      );
    } else if (spaceObject.funnel) {
      acc.push(
        toLibraryObject({
          spaceObject,
          recordObj: spaceObject.funnel?.funnel?.funnel,
          canned: false, // we dont have canned Funnels
          starred: spaceObject.funnel?.starred ?? false,
          metadataObj: spaceObject.funnel?.funnel?.metadata,
        }),
      );
    } else if (spaceObject.retentionMetric) {
      acc.push(
        toLibraryObject({
          spaceObject,
          recordObj: spaceObject.retentionMetric?.retentionMetric?.retentionMetric,
          canned: false, // we dont have canned Retentions
          starred: spaceObject.retentionMetric?.starred ?? false,
          metadataObj: spaceObject.retentionMetric?.retentionMetric?.metadata,
        }),
      );
    } else if (spaceObject.segment) {
      acc.push(
        toLibraryObject({
          spaceObject,
          recordObj: spaceObject.segment?.segment?.segment,
          canned: spaceObject.segment.segment?.segmentMetadata?.canned ?? false,
          starred: spaceObject.segment?.starred ?? false,
          metadataObj: spaceObject.segment?.segment?.metadata,
        }),
      );
    } else if (spaceObject.dashboard) {
      acc.push(
        toLibraryObject({
          spaceObject,
          recordObj: spaceObject.dashboard,
          canned: spaceObject.dashboard.isCanned,
          starred: spaceObject.dashboard?.starred ?? false,
          metadataObj: spaceObject.dashboard?.metadata
            ? {
                created: spaceObject.dashboard.metadata.createdAt,
                createdBy: spaceObject.dashboard.metadata.createdBy,
                lastUpdated: spaceObject.dashboard.metadata.updatedAt,
                lastUpdatedBy: spaceObject.dashboard.metadata.updatedBy,
                // dashboards cannot be archived
                archived: false,
                visibility: spaceObject.dashboard.visibility,
              }
            : null,
        }),
      );
    } else if (spaceObject.journey) {
      acc.push(
        toLibraryObject({
          spaceObject,
          recordObj: spaceObject.journey?.journey?.journeyMap,
          canned: false, // we dont have canned Journeys
          starred: spaceObject.journey?.starred ?? false,
          metadataObj: spaceObject.journey?.journey?.metadata,
        }),
      );
    } else if (spaceObject.heatmap) {
      acc.push(
        toLibraryObject({
          spaceObject,
          recordObj: spaceObject.heatmap?.heatmap?.heatmap,
          canned: false, // we dont have canned Heatmaps
          starred: spaceObject.heatmap?.starred ?? false,
          metadataObj: spaceObject.heatmap?.heatmap?.metadata,
        }),
      );
    } else if (spaceObject.dataTable) {
      acc.push(
        toLibraryObject({
          spaceObject,
          recordObj: spaceObject.dataTable?.dataTable?.dataTable,
          canned: false, // we dont have canned Data Tables
          starred: spaceObject.dataTable?.starred ?? false,
          metadataObj: spaceObject.dataTable?.dataTable?.metadata,
        }),
      );
    }

    return acc;
  }, []);
};

/**
 * The set of search inputs that can be used to search DX Objects.
 *
 * A null value means no filter, or All
 */
export type LibraryObjectSearchParams = {
  filter: FilterOptionValueType;
  search: string;
  sort: {
    sortColumnKey: string;
    sortDirection: SortDirection;
  };
  /**
   * null means no filter exists
   */
  objectTypes: LibraryObjectType[] | null;
  spaceId: string | null;
  /**
   * Anything invalid (not an integer or less than 1) will default to 1.
   *
   * Not zero-based!
   *
   * @default 1
   */
  page: number;
};

// this will hold on to state of the previous request page info, and tokens for the prior and next page.
type LastRequestPageInfo = Pick<PageInfoResponse, 'nextPageToken' | 'previousPageToken' | 'page'>;

type Data = {
  /**
   * undefined when no data has not come back yet.
   */
  objects: LibraryObject[] | undefined;
  matchingRecords: PageInfoResponse['matchingRecords'];
  totalRecords: PageInfoResponse['totalRecords'];
  page: number;
};

type LibraryObjectSearchResult = Pick<
  ReturnType<typeof useXhrRequest>,
  'loading' | 'error' | 'refetch'
> &
  Data;

const SPACE_LIBRARY_FILTERS: { [type in LibraryObjectType]: SpaceLibraryFilter } = {
  [ObjectType.METRIC]: SpaceLibraryFilter.SPACES_FILTER_OBJECT_METRICS,
  [ObjectType.FUNNEL]: SpaceLibraryFilter.SPACES_FILTER_OBJECT_FUNNELS,
  [ObjectType.RETENTION]: SpaceLibraryFilter.SPACES_FILTER_OBJECT_RETENTION_METRICS,
  [ObjectType.SEGMENT]: SpaceLibraryFilter.SPACES_FILTER_OBJECT_SEGMENTS,
  [ObjectType.DASHBOARD]: SpaceLibraryFilter.SPACES_FILTER_OBJECT_DASHBOARDS,
  [ObjectType.HEATMAP]: SpaceLibraryFilter.SPACES_FILTER_OBJECT_HEATMAPS,
  [ObjectType.JOURNEY]: SpaceLibraryFilter.SPACES_FILTER_OBJECT_JOURNEYS,
  [ObjectType.DATA_TABLE]: SpaceLibraryFilter.SPACES_FILTER_OBJECT_DATA_TABLES,
};

const FILTER_MAP: { [type in FilterOptionValueType]: SpaceLibraryFilter } = {
  active: SpaceLibraryFilter.SPACES_FILTER_ACTIVE,
  archived: SpaceLibraryFilter.SPACES_FILTER_ARCHIVED,
  recent: SpaceLibraryFilter.SPACES_FILTER_RECENT,
  starred: SpaceLibraryFilter.SPACES_FILTER_STARRED,
  mine: SpaceLibraryFilter.SPACES_FILTER_CREATED_BY_ME,
  private: SpaceLibraryFilter.SPACES_FILTER_PRIVATE,
  hasInsights: SpaceLibraryFilter.SPACES_FILTER_HAS_INSIGHTS,
};

const SORT_BY_COLUMNS: { [key: string]: SpaceObjectOrder.By } = {
  [LIBRARY_TABLE_LAST_MODIFIED_COLUMN_KEY]: SpaceObjectOrder.By.BY_LAST_MODIFIED,
  [LIBRARY_TABLE_NAME_COLUMN_KEY]: SpaceObjectOrder.By.BY_NAME,
  [LIBRARY_TABLE_INSIGHTS_COLUMN_KEY]: SpaceObjectOrder.By.BY_METRIC_INSIGHT_COUNT,
  [LIBRARY_TABLE_SPACE_COLUMN_KEY]: SpaceObjectOrder.By.BY_OWNING_SPACE_NAME,
  [LIBRARY_TABLE_CREATOR_COLUMN_KEY]: SpaceObjectOrder.By.BY_CREATED_BY,
} as const;

const getPageToken = (page: number, lastRequestPageInfo: LastRequestPageInfo | undefined) => {
  if (!lastRequestPageInfo) return null;

  // previous page
  if (page + 1 === lastRequestPageInfo.page) {
    return lastRequestPageInfo.previousPageToken;
  }

  // next page
  if (page - 1 === lastRequestPageInfo.page) {
    return lastRequestPageInfo.nextPageToken;
  }

  return null;
};

export const useLibraryObjectSearch = (
  params: LibraryObjectSearchParams,
  pageSize: number,
  excludeReferences = false,
  enabled: boolean = true,
  excludePrivateObjects = false,
): LibraryObjectSearchResult => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(fullstorySelectors.getOrgId);

  // the pagination token should be an internal implementation detail of this hook
  const lastRequestPageInfo = useRef<LastRequestPageInfo | undefined>(undefined);

  // reset pagination token ahead of the request when the space or params change
  // params should exclude page
  useEffect(() => {
    lastRequestPageInfo.current = undefined;
  }, [
    params.spaceId,
    params.filter,
    params.objectTypes,
    params.search,
    params.sort,
    excludeReferences,
  ]);

  const makeRequest = useCallback(() => {
    if (!enabled) return null;

    return SpaceRequestActions.getLibraryView(
      {
        orgId,
        id: params.spaceId ?? '',
        filters: [
          ...(excludePrivateObjects ? [SpaceLibraryFilter.SPACES_FILTER_PUBLIC] : []),
          FILTER_MAP[params.filter],
          // object types represent a different group of filters on the server
          ...(!!params.objectTypes && params.objectTypes.length
            ? params.objectTypes.map(t => SPACE_LIBRARY_FILTERS[t])
            : [SpaceLibraryFilter.SPACES_FILTER_OBJECT_ALL]),
        ],
        // sort should be set by the client so it can also default the Table UI accordingly
        order: {
          // shouldn't need the fallback, its just there due to string key typing in the SORT_BY_COLUMNS
          by:
            SORT_BY_COLUMNS[params.sort.sortColumnKey ?? ''] ??
            SpaceObjectOrder.By.BY_LAST_MODIFIED,
          direction:
            params.sort.sortDirection === 'asc'
              ? SpaceObjectOrder.Direction.DIRECTION_ASC
              : SpaceObjectOrder.Direction.DIRECTION_DESC,
        },
        query: params.search.trim(),
        pageInfo: {
          pageToken: getPageToken(params.page, lastRequestPageInfo.current) ?? '',
          pageSize,
          skip: (params.page - 1) * pageSize,
        },
        excludeReferences,
        cacheOptions: {
          skipCache: shouldGetDxObjectsRequestSkipCache(LIBRARY_OBJECT_SEARCH_REQUEST_KEY),
        },
      },
      true,
    );
  }, [
    enabled,
    excludeReferences,
    excludePrivateObjects,
    orgId,
    pageSize,
    params.filter,
    params.objectTypes,
    params.page,
    params.search,
    params.sort.sortColumnKey,
    params.sort.sortDirection,
    params.spaceId,
  ]);

  // using state to accomodate the refetch scenario when skipCache has changed values in a ref, see below
  const [request, setRequest] = useState(() => makeRequest());
  useEffect(() => {
    setRequest(prev => {
      const next = makeRequest();
      // this is especially useful to not cancel and re-issue the initial request
      return isEqual(prev?.request, next?.request) ? prev : next;
    });
  }, [makeRequest]);

  const { data, loading, error, refetch } = useXhrRequest(request);

  // everytime data changes means a new response has come back
  useEffect(() => {
    if (data) {
      dispatch(DxObjectsCacheActions.markResponseTime(LIBRARY_OBJECT_SEARCH_REQUEST_KEY));
      lastRequestPageInfo.current = {
        // server response is zero-based
        page: (data?.pageInfo?.page ?? 0) + 1,
        previousPageToken: data.pageInfo?.previousPageToken ?? null,
        nextPageToken: data.pageInfo?.nextPageToken ?? null,
      };
    }
  }, [dispatch, data]);

  // need to re-create the request object if the skip cache value has changed
  const handleRefetch = useCallback(() => {
    if (
      request?.request.body?.cacheOptions?.skipCache ===
      shouldGetDxObjectsRequestSkipCache(LIBRARY_OBJECT_SEARCH_REQUEST_KEY)
    ) {
      // the request object doesnt need to change, execute the normal refetch function
      refetch();
    } else {
      // recreate the request object with the current skipCache value
      // useXhrRequest will hook into this and issue another request
      setRequest(makeRequest());
    }
  }, [makeRequest, refetch, request]);

  // re-issue requests on window (tab) focus if the cache has been invalidated
  useEffect(() => {
    const onWindowFocus = () => {
      if (shouldGetDxObjectsRequestSkipCache(LIBRARY_OBJECT_SEARCH_REQUEST_KEY)) {
        // making a new request object will trigger useXhrRequest to issue the new request
        setRequest(makeRequest());
      }
    };

    window.addEventListener('focus', onWindowFocus);

    return () => {
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [makeRequest, request]);

  const resultData = useMemo(
    (): Data => ({
      objects: data?.objects ? toLibraryObjects(data.objects) : undefined,
      matchingRecords: data?.pageInfo?.matchingRecords ?? 0,
      totalRecords: data?.pageInfo?.totalRecords ?? 0,
      // server response is zero-based
      page: (data?.pageInfo?.page ?? 0) + 1,
    }),
    [data],
  );

  return {
    loading,
    ...resultData,
    error,
    refetch: handleRefetch,
  };
};
